import React from "react";
import AssessmentIcon from "@material-ui/icons/Assessment";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SpeedIcon from "@material-ui/icons/Speed";
import WarningIcon from "@material-ui/icons/Warning";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalysisMetrics } from "../../types";
import { SUCCESS_RATE_TARGET } from "../../constants";
import { KpiGrid, KpiCard } from "../../styles";

import { ScreenPlatform } from "@store/ducks/Screen/screen.type";

/** Props for the KpiCards molecule. */
interface KpiCardsProps {
  metrics: TravelAnalysisMetrics | null;
  loading: boolean;
  platform: ScreenPlatform;
  onAlertsClick?: () => void;
}

/** Accent colors for each KPI card's left border. */
const KPI_COLORS = {
  total: "#002951",
  success: "#4caf50",
  rate: "#1a73e8",
  alerts: "#ff6c5c"
};

/**
 * Renders the 4 KPI summary cards matching the prototype:
 * background icon, label on top, large value, META tag + progress bar on rate card.
 */
const KpiCards: React.FC<KpiCardsProps> = ({ metrics, loading, platform, onAlertsClick }) => {

  const { t } = useTranslation();
  const placeholder = "—";

  const totalAnalyzed = metrics ? metrics.totalAnalyzed.toLocaleString("pt-BR") : placeholder;
  const successCount = metrics ? metrics.successCount.toLocaleString("pt-BR") : placeholder;
  const successRate = metrics ? `${metrics.successRate.toFixed(1)}%` : placeholder;
  const alertsDetected = metrics
    ? (metrics.totalAnalyzed - metrics.successCount).toLocaleString("pt-BR")
    : placeholder;

  const successPercent = metrics && metrics.totalAnalyzed > 0
    ? `${((metrics.successCount / metrics.totalAnalyzed) * 100).toFixed(0)}% ${t(TravelAnalysisMessages.percentOfTotal)}`
    : "";

  const ratePercent = metrics ? Math.min(metrics.successRate, 100) : 0;

  return (
    <KpiGrid platform={platform}>
      <KpiCard accentColor={KPI_COLORS.total}>
        <span className="kpi-bg-icon"><AssessmentIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.totalAnalyzed)}</span>
          <span className="kpi-value">{totalAnalyzed}</span>
          <span className="kpi-secondary">{t(TravelAnalysisMessages.tripsUnit)}</span>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.success}>
        <span className="kpi-bg-icon"><CheckCircleOutlineIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.successfulTrips)}</span>
          <div className="kpi-value-row">
            <span className="kpi-value">{successCount}</span>
            {successPercent && <span className="kpi-secondary">{successPercent}</span>}
          </div>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.rate}>
        <span className="kpi-bg-icon"><SpeedIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.successRate)}</span>
          <div className="kpi-value-row">
            <span className="kpi-value">{successRate}</span>
            <span className="kpi-meta-tag">{t(TravelAnalysisMessages.target)}: {SUCCESS_RATE_TARGET}%</span>
          </div>
          <div className="kpi-progress-track">
            <div className="kpi-progress-fill" style={{ width: `${ratePercent}%` }} />
          </div>
        </div>
      </KpiCard>
      <KpiCard accentColor={KPI_COLORS.alerts} clickable={!!onAlertsClick} onClick={onAlertsClick}>
        <span className="kpi-bg-icon"><WarningIcon fontSize="inherit" /></span>
        <div className="kpi-content">
          <span className="kpi-label">{t(TravelAnalysisMessages.alertsDetected)}</span>
          <span className="kpi-value">{alertsDetected}</span>
          <span className="kpi-secondary">{t(TravelAnalysisMessages.tripsUnit)}</span>
        </div>
      </KpiCard>
    </KpiGrid>
  );
};

export default KpiCards;
